import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { resumeAPI } from '../services/api';
import toast from 'react-hot-toast';
import { ArrowLeft, Save } from 'lucide-react';

const FIELDS = [
  { section: 'Personal Information', fields: [
    { key: 'fname', label: 'First Name', required: true },
    { key: 'mname', label: 'Middle Name' },
    { key: 'lname', label: 'Last Name', required: true },
    { key: 'email', label: 'Email' },
    { key: 'city', label: 'City' },
    { key: 'state', label: 'State' },
    { key: 'zip', label: 'Zip' },
    { key: 'country', label: 'Country' },
    { key: 'location', label: 'Location' },
    { key: 'loccontry', label: 'Location Country Code' },
  ]},
  { section: 'Visa & Status', fields: [
    { key: 'visastatus', label: 'Visa Status' },
    { key: 'vdate', label: 'Visa Date' },
    { key: 'condition', label: 'Condition' },
    { key: 'hold1', label: 'Hold' },
    { key: 'sendmail', label: 'Send Mail Flag' },
    { key: 'idstring', label: 'ID String' },
  ]},
  { section: 'Employment', fields: [
    { key: 'employer', label: 'Employer' },
    { key: 'vendorname', label: 'Vendor Name' },
    { key: 'requestid', label: 'Request ID' },
    { key: 'rate', label: 'Rate' },
    { key: 'unit1', label: 'Rate Unit' },
    { key: 'intcontact', label: 'Internal Contact' },
    { key: 'usexp', label: 'US Experience' },
  ]},
  { section: 'Availability', fields: [
    { key: 'availablty', label: 'Availability' },
    { key: 'dtavail', label: 'Date Available' },
    { key: 'dtsubmit', label: 'Date Submitted' },
    { key: 'relocation', label: 'Relocation' },
  ]},
];

const TEXTAREA_FIELDS = [
  { section: 'Details', fields: [
    { key: 'comment', label: 'Comments' },
    { key: 'csgcomment', label: 'CSG Comment', type: 'input' },
    { key: 'resume', label: 'Resume Text' },
    { key: 'reference', label: 'References' },
    { key: 'removedesc', label: 'Remove Description', type: 'input' },
    { key: 'newstate', label: 'New State', type: 'input' },
  ]},
];

export default function ResumeFormPage() {
  const { recno } = useParams();
  const navigate = useNavigate();
  const isEditing = !!recno;
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEditing);

  useEffect(() => {
    if (isEditing) {
      resumeAPI.get(recno).then(({ data }) => { setForm(data); setLoading(false); })
        .catch(() => { toast.error('Failed to load'); navigate('/search'); });
    }
  }, [recno, isEditing, navigate]);

  const handleChange = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.fname || !form.lname) { toast.error('First and last name required'); return; }
    setSaving(true);
    try {
      if (isEditing) { await resumeAPI.update(recno, form); toast.success('Updated'); navigate(`/resumes/${recno}`); }
      else { const { data } = await resumeAPI.create(form); toast.success('Created'); navigate(`/resumes/${data.recno}`); }
    } catch (err) {
      const errors = err.response?.data;
      if (errors && typeof errors === 'object') toast.error(Object.entries(errors).map(([k, v]) => `${k}: ${Array.isArray(v) ? v[0] : v}`).join(', '));
      else toast.error('Failed to save');
    }
    setSaving(false);
  };

  if (loading) return <div className="page-container"><div className="loading-spinner"><div className="spinner" /></div></div>;

  return (
    <div className="page-container" style={{ maxWidth: 960 }}>
      <button className="btn btn-ghost" onClick={() => navigate(-1)} style={{ marginBottom: 20 }}><ArrowLeft size={18} /> Back</button>
      <div className="page-header"><div><h1 className="page-title">{isEditing ? 'Edit Resume' : 'Add New Resume'}</h1></div></div>

      <form onSubmit={handleSubmit}>
        {FIELDS.map(section => (
          <div className="card" style={{ marginBottom: 24 }} key={section.section}>
            <div className="card-body">
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-heading)', marginBottom: 20 }}>{section.section}</h3>
              <div className="form-row">
                {section.fields.map(f => (
                  <div className="form-group" key={f.key}>
                    <label className="form-label">{f.label} {f.required && '*'}</label>
                    <input className="form-input" value={form[f.key] || ''} onChange={e => handleChange(f.key, e.target.value)} required={f.required} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}

        {TEXTAREA_FIELDS.map(section => (
          <div className="card" style={{ marginBottom: 24 }} key={section.section}>
            <div className="card-body">
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-heading)', marginBottom: 20 }}>{section.section}</h3>
              {section.fields.map(f => (
                <div className="form-group" key={f.key}>
                  <label className="form-label">{f.label}</label>
                  {f.type === 'input' ? (
                    <input className="form-input" value={form[f.key] || ''} onChange={e => handleChange(f.key, e.target.value)} />
                  ) : (
                    <textarea className="form-textarea" value={form[f.key] || ''} onChange={e => handleChange(f.key, e.target.value)} rows={6} />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
          <button type="button" className="btn btn-secondary" onClick={() => navigate(-1)}>Cancel</button>
          <button type="submit" className="btn btn-primary btn-lg" disabled={saving}><Save size={18} /> {saving ? 'Saving...' : isEditing ? 'Update' : 'Create'}</button>
        </div>
      </form>
    </div>
  );
}
